
import React, { useEffect, useState } from "react";
import usersApi, { ReportInterval } from "../services/users/UserApi";

interface FeedbackSender {
  id: number;
  firstName: string;
  email: string;
  companyName: string;
  website: string;
  noOfEmployees: number;
  type: string;
  status: string;
}

interface FeedbackItem {
  id: number;
  sender: FeedbackSender;
  subject: string;
  description: string;
}


const FeedbackPage = () => {
  const [activeTab, setActiveTab] = useState<'feedback' | 'suggestions'>('feedback');
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [suggestions, setSuggestions] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<ReportInterval>(ReportInterval.DAILY);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  // Removed unused pageNumber state

  const fetchData = async (tab: 'feedback' | 'suggestions', filters?: { interval?: ReportInterval; startDate?: string; endDate?: string }) => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (interval) params.type = interval;
      if (interval === ReportInterval.CUSTOM) {
        if (startDate) params.startTime = new Date(startDate).getTime();
        if (endDate) params.endTime = new Date(endDate).getTime();
      }
      // For demo, use same API for both tabs, but filter by subject for suggestions
      const res = await usersApi.getFeedbackList(params);
      if (res.success && res.value && res.value.data && Array.isArray(res.value.data.contents)) {
        if (tab === 'feedback') {
          setFeedback(res.value.data.contents.filter((item: any) => !item.subject?.toLowerCase().includes('suggestion')));
        } else {
          setSuggestions(res.value.data.contents.filter((item: any) => item.subject?.toLowerCase().includes('suggestion')));
        }
      } else {
        setError("Invalid response from server");
      }
    } catch (err: any) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
    // eslint-disable-next-line
  }, [activeTab]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData(activeTab, { interval, startDate, endDate });
  };

  return (
    <div className="p-8">     
      <div className="mb-6 flex gap-4">
        <button
          className={`px-4 py-2 rounded ${activeTab === 'feedback' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback
        </button>
        <button
          className={`px-4 py-2 rounded ${activeTab === 'suggestions' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('suggestions')}
        >
          Suggestions
        </button>
      </div>
      <div>
         <h2 className="text-2xl font-bold mb-6">{activeTab === 'feedback' ? "Feedback" : "Suggestions"} </h2>
      </div>
      <form className="flex flex-wrap gap-4 mb-6 items-end" onSubmit={handleFilter}>
        <div>
          <label className="block text-xs font-semibold mb-1">Interval</label>
          <select
            className="p-2 rounded border"
            value={interval}
            onChange={e => setInterval(e.target.value as ReportInterval)}
          >
            {Object.values(ReportInterval).map((val) => (
              <option key={val} value={val}>{val}</option>
            ))}
          </select>
        </div>
        {interval === ReportInterval.CUSTOM && (
          <>
            <div>
              <label className="block text-xs font-semibold mb-1">Start Date</label>
              <input
                type="date"
                className="p-2 rounded border"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">End Date</label>
              <input
                type="date"
                className="p-2 rounded border"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </>
        )}
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Filter</button>
      </form>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b">ID</th>
              <th className="py-2 px-4 border-b">User</th>
              <th className="py-2 px-4 border-b">Email</th>
              <th className="py-2 px-4 border-b">Subject</th>
              <th className="py-2 px-4 border-b">Description</th>
            </tr>
          </thead>
          <tbody>
            {(activeTab === 'feedback' ? feedback : suggestions).map(item => (
              <tr key={item.id}>
                <td className="py-2 px-4 border-b text-center">{item.id}</td>
                <td className="py-2 px-4 border-b">{item.sender.firstName}</td>
                <td className="py-2 px-4 border-b">{item.sender.email}</td>
                <td className="py-2 px-4 border-b">{item.subject}</td>
                <td className="py-2 px-4 border-b">{item.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FeedbackPage;
